import { useMemo } from 'react';
import { useAppSelector } from 'src/utils/Hooks';

/**
 * Custom hook to enhance entity metadata with category and subcategory information from Redux
 *
 * @returns Object containing helper functions and lookup maps for entity metadata enhancement
 */
export const useEntityMetadataEnhancer = () => {
  // Get categories and subcategories from Redux
  const categories = useAppSelector((state) => state.categories);
  const subCategories = useAppSelector((state) => state.subCategories);

  // Create lookup maps for categories and subcategories
  const categoryMap = useMemo(() => {
    return categories.reduce((map, category) => {
      map[category.categoryId] = category;
      return map;
    }, {} as Record<number, (typeof categories)[0]>);
  }, [categories]);

  const subCategoryMap = useMemo(() => {
    return subCategories.reduce((map, subCategory) => {
      map[subCategory.subCategoryId] = subCategory;
      return map;
    }, {} as Record<number, (typeof subCategories)[0]>);
  }, [subCategories]);

  /**
   * Enhance metadata based on field type
   *
   * @param datum - The data item to enhance
   * @param fieldName - The field name (e.g., 'category', 'subcategory', 'brand')
   * @returns Enhanced metadata object with appropriate properties based on field type
   */
  const enhanceMetadata = (datum: any, fieldName: string) => {
    const fieldNameLower = fieldName.toLowerCase();
    const fieldKey = datum?.metadata?.id || datum?.fieldId;

    const baseMetadata = {
      name: datum?.metadata?.name,
      id: fieldKey,
      type: fieldName,
      searchTerm: datum?.metadata?.searchTerm || fieldKey
    };

    // Handle category fields
    if (fieldNameLower === 'categoryid' || fieldNameLower === 'category') {
      const categoryId = Number(fieldKey);
      const category = categoryMap[categoryId];

      if (category) {
        return {
          ...baseMetadata,
          name: category.name,
          categoryId: categoryId,
          categoryName: category.name
        };
      }
    }

    // Handle subcategory fields
    if (fieldNameLower === 'subcategoryid' || fieldNameLower === 'subcategory') {
      const subCategoryId = Number(fieldKey);
      const subCategory = subCategoryMap[subCategoryId];

      if (subCategory) {
        return {
          ...baseMetadata,
          name: subCategory.displayName,
          subCategoryId: subCategoryId,
          subCategoryName: subCategory.displayName,
          parentCategoryId: (subCategory as any).parentCategoryId
        };
      }
    }

    // Handle brand fields
    if (fieldNameLower === 'brand') {
      return {
        ...baseMetadata,
        brandId: Number(fieldKey),
        brandName: datum?.metadata?.name || `${fieldKey}`
      };
    }

    // Default case
    return baseMetadata;
  };

  /**
   * Get display name for an entity based on field type
   *
   * @param fieldId - The entity ID
   * @param fieldName - The field name (e.g., 'category', 'subcategory', 'brand')
   * @returns Display name for the entity
   */
  const getEntityDisplayName = (fieldId: string | number, fieldName: string): string => {
    const fieldNameLower = fieldName.toLowerCase();

    if (fieldNameLower === 'categoryid' || fieldNameLower === 'category') {
      const categoryId = Number(fieldId);
      return categoryMap[categoryId]?.name ?? `${fieldId}`;
    }

    if (fieldNameLower === 'subcategoryid' || fieldNameLower === 'subcategory') {
      const subCategoryId = Number(fieldId);
      return subCategoryMap[subCategoryId]?.displayName ?? `${fieldId}`;
    }

    return `${fieldId}`;
  };

  return {
    categories,
    subCategories,
    categoryMap,
    subCategoryMap,
    enhanceMetadata,
    getEntityDisplayName
  };
};
